import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import { granite, grayLight, moovsBlue } from "globals/design-system/colors";
import { DriverScheduleEvent } from "types";
import { REMOVE_DRIVER_SCHEDULE_SETTING_AND_EVENTS_MUTATION } from "globals/graphql";
import { useSnackbar } from "globals/hooks/useSnackbar";

type DeleteConfirmationDialogProps = {
  open: boolean;
  onConfirmDeleteEventDialogClose: () => void;
  event: DriverScheduleEvent;
  onDeleteEventDialogClose: () => void;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
};

function DeleteConfirmationDialog(props: DeleteConfirmationDialogProps) {
  const {
    open,
    onConfirmDeleteEventDialogClose,
    event,
    onDeleteEventDialogClose,
    startPolling,
    stopPolling,
  } = props;

  const eventIsRecurring = !!event?.driverScheduleSetting?.daysToRepeat;

  // hooks
  const snackbar = useSnackbar();

  // state
  const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // mutation
  const [removeDriverScheduleSettingAndEvents] = useMutation(
    REMOVE_DRIVER_SCHEDULE_SETTING_AND_EVENTS_MUTATION,
    {
      onCompleted: () => {
        startPolling(1000);

        const timeout = setTimeout(() => {
          stopPolling();
        }, 3000);

        setPollingTimeout(timeout);

        snackbar.success("Event was deleted successfully");

        onConfirmDeleteEventDialogClose();
        onDeleteEventDialogClose();
      },
    }
  );

  // event handlers
  const handleConfirmClick = () => {
    removeDriverScheduleSettingAndEvents({
      variables: {
        input: { driverScheduleEventId: event.id },
      },
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [pollingTimeout]);

  return (
    <Dialog
      open={open}
      onClose={onConfirmDeleteEventDialogClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 320,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h3" fontWeight={600}>
          Delete Event
        </Typography>
        <IconButton onClick={onConfirmDeleteEventDialogClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color={granite} mt={2}>
          {eventIsRecurring
            ? "Are you sure you want to delete these recurring events?"
            : "Are you sure you want to delete?"}
        </Typography>
      </DialogContent>

      <Divider sx={{ borderColor: grayLight }} />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onConfirmDeleteEventDialogClose}
          sx={{
            color: moovsBlue,
            borderColor: moovsBlue,
            "&:hover": {
              borderColor: moovsBlue,
              bgcolor: "rgba(0, 123, 255, 0.04)",
            },
          }}
        >
          No, keep
        </Button>
        <Button
          onClick={handleConfirmClick}
          variant="contained"
          sx={{
            bgcolor: moovsBlue,
            color: "white",
            "&:hover": {
              bgcolor: moovsBlue,
              opacity: 0.9,
            },
          }}
        >
          {eventIsRecurring ? "Yes, delete all" : "Yes, delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteConfirmationDialog;
